import { createRouter, createWebHistory } from 'vue-router';
import { i18n, vuetify } from '@/main';
import { useLoginStore } from '@/stores/loginStore';
import AuthRoutes from '@/router/AuthRoutes';
import { RouteNamesEnum } from '@/enums/RouteNamesEnum';
import HomeRoutes from '@/router/HomeRoutes';
import SettingsRoutes from '@/router/SettingsRoutes';
import useUserSession from '@/composables/useUserSession';
import ListsRoutes from '@/router/ListsRoutes';
import CalibrationsRoutes from '@/router/CalibrationsRoutes';
import OrdersRoutes from '@/router/OrdersRoutes';
import { supportedLanguages, type SupportedLanguage } from '@/languages';
import * as yup from 'yup';
import yupLocale from '@/plugins/yup/locale';

const routes = [
    ...AuthRoutes,
    ...HomeRoutes,
    ...SettingsRoutes,
    ...ListsRoutes,
    ...CalibrationsRoutes,
    ...OrdersRoutes,
    {
        path: '/my-account',
        name: RouteNamesEnum.MyAccount,
        component: () => import('../views/general/MyAccountView.vue'),
        meta: {
            title: 'MenuComponent.my-account',
            auth: {
                required: true,
            },
            showInMenu: false,
        },
    },
    {
        path: '/unauthorized',
        name: 'unauthorized',
        component: () => import('../views/UnauthorizedView.vue'),
        meta: {
            title: 'UnauthorizedView.title',
            auth: {
                required: true, //NOTE: po importu Newe projektu tady bylo puvodne false (predelano kvuli zobrazeni hlavniho menu), pokud to bude zlobit, tak vratit zpet
            },
            showInMenu: false,
        },
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('../views/NotFoundView.vue'),
        meta: {
            title: 'NotFoundView.title',
            auth: {
                required: true,
            },
            showInMenu: false,
        },
    },
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

const { getLanguage, getIsAuthenticated, getRefreshToken, isAccessTokenExpired, isTwoFactorVerified } =
    useUserSession();

window.onfocus = () => {
    if (getIsAuthenticated() && isAccessTokenExpired()) {
        const storeLogin = useLoginStore();
        storeLogin.refreshCredentials();
    }
};

router.beforeEach(async (to, from, next) => {
    const storeLogin = useLoginStore();

    const language = getLanguage();
    const locale = (language && supportedLanguages.includes(language as SupportedLanguage)) ? language : import.meta.env.VITE_APP_DEFAULT_LANGUAGE;
    i18n.global.locale.value = locale as SupportedLanguage;
    vuetify.locale.current.value = locale;
    yup.setLocale(yupLocale(i18n.global.t));
    document.title = (i18n.global.t(to.meta.title as string) + ' | Calibratech') as string;

    //vyzaduje stranka prihlaseni
    if ((to.meta.auth as { required: boolean }).required) {
        if (!isTwoFactorVerified()) {
            next('/');
            return;
        }

        //je prihlasen
        if (getIsAuthenticated() && !isAccessTokenExpired()) {
            next();
        }
        //neni prihlasen
        else {
            //je to po page refresh
            if (getRefreshToken()) {
                await storeLogin
                    .refreshCredentials(true)
                    .then(() => {
                        next();
                    })
                    .catch((error) => {
                        alert(error);
                        next('/');
                    });
            }
            //nebyl prihlasen ani pred tim
            else {
                next('/');
            }
        }
    }
    //stranka nevyzaduje prihlaseni
    else {
        //je prihlasen
        if (getIsAuthenticated()) {
            //chce jit na login
            if (to.path === '/') {
                document.title = (i18n.global.t('DevicesModule.module-name' as string) + ' | Calibratech') as string;
                next(RouteNamesEnum.Home);
            }
            //chce jit jinam (treba gdpr)
            else {
                next();
            }
        }
        //neni prihlasen a stranka nevyzaduje prihlaseni
        else {
            //ale byl prihlasen -> prisel po refresh
            if (getRefreshToken() && isTwoFactorVerified()) {
                //chce jit na login
                if (to.path === '/') {
                    await storeLogin
                        .refreshCredentials(true)
                        .then(() => {
                            document.title = (i18n.global.t('DevicesModule.module-name' as string) + ' | Calibratech') as string;
                            next(RouteNamesEnum.Home);
                        })
                        .catch((error) => {
                            alert(error);
                            next();
                        });
                }
                //nechce na login
                else {
                    storeLogin.refreshCredentials();
                    next();
                }
            }
            //nebyl prihlasen ani pred tim
            else {
                next();
            }
        }
    }
});

export default router;
